import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Skeleton } from './ui/skeleton';
import { SocialShare } from './SocialShare';
import { User, Image, Video, Quote, Heart, Share2, TrendingUp } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PostsGallery = ({ refreshTrigger }) => {
  const { t, language } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ approved: 0, total_shares: 0 });
  const [expandedShare, setExpandedShare] = useState(null);

  const fetchPosts = async () => {
    try {
      const [postsRes, statsRes] = await Promise.all([
        axios.get(`${API}/posts/approved`),
        axios.get(`${API}/posts/stats`)
      ]);
      setPosts(postsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [refreshTrigger]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Random rotation for postcard effect
  const getRotation = (index) => {
    const rotations = ['-rotate-1', 'rotate-1', '-rotate-2', 'rotate-2', 'rotate-0', '-rotate-1'];
    return rotations[index % rotations.length];
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <Skeleton className="h-8 w-64 mx-auto mb-2" />
          <Skeleton className="h-4 w-32 mx-auto" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-64 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="posts-gallery">
      {/* Section Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 bg-[#009639]/10 text-[#009639] px-4 py-2 rounded-full text-sm font-medium mb-4">
          <Heart className="w-4 h-4 animate-heartbeat" />
          <span>{language === 'ar' ? 'رسائل من القلب' : 'Messages from the Heart'}</span>
        </div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-[#0a0a0a] mb-3">
          {t('galleryTitle')}
        </h2>
        
        {/* Stats Row */}
        <div className="flex items-center justify-center gap-6 mt-4">
          {stats.approved > 0 && (
            <p className="text-[#009639] font-medium text-lg">
              {stats.approved} {t('messagesShared')}
            </p>
          )}
          {stats.total_shares > 0 && (
            <div className="flex items-center gap-2 text-gray-500">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm font-medium">
                {stats.total_shares} {language === 'ar' ? 'مشاركة' : 'shares'}
              </span>
            </div>
          )}
        </div>
      </div>

      {posts.length === 0 ? (
        <div className="text-center py-20" data-testid="no-posts">
          <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-[#009639]/10 flex items-center justify-center">
            <Quote className="w-12 h-12 text-[#009639]" />
          </div>
          <p className="text-gray-500 text-xl mb-2">{t('noMessages')}</p>
          <p className="text-gray-400 text-sm">{language === 'ar' ? 'كن أول من يشارك حبه' : 'Be the first to share your love'}</p>
        </div>
      ) : (
        /* Postcard Collage Grid */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {posts.map((post, index) => (
            <div
              key={post.id}
              className={`postcard-item ${getRotation(index)} hover:rotate-0 transition-all duration-300`}
              style={{ animationDelay: `${index * 0.1}s` }}
              data-testid={`post-card-${post.id}`}
            >
              <div className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 group">
                {/* Media */}
                {post.media_url ? (
                  <div className="relative aspect-[4/3] bg-gray-100 overflow-hidden">
                    {post.media_type === 'image' ? (
                      <img
                        src={`${BACKEND_URL}${post.media_url}`}
                        alt={`Support from ${post.name}`}
                        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    ) : (
                      <video
                        src={`${BACKEND_URL}${post.media_url}`}
                        className="w-full h-full object-cover"
                        controls
                        preload="metadata"
                      />
                    )}
                    {/* Media type badge */}
                    <div className="absolute top-3 right-3 bg-black/50 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
                      {post.media_type === 'image' ? <Image className="w-3 h-3" /> : <Video className="w-3 h-3" />}
                    </div>
                  </div>
                ) : (
                  /* No media - text only postcard */
                  <div className="relative aspect-[4/3] bg-gradient-to-br from-gray-50 to-white flex items-center justify-center p-6">
                    <Quote className="absolute top-4 left-4 w-8 h-8 text-[#009639]/20" />
                    <p className="text-[#0a0a0a] text-lg font-medium text-center leading-relaxed line-clamp-4" dir={language === 'ar' ? 'rtl' : 'ltr'}>
                      "{post.text}"
                    </p>
                  </div>
                )}

                {/* Content */}
                <div className="p-4 bg-white">
                  {/* Quote - only show if has media */}
                  {post.media_url && (
                    <div className={`mb-3 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                      <p className="text-[#009639] text-xs font-medium mb-1">
                        {language === 'ar' ? 'الإمارات هي...' : 'The UAE is...'}
                      </p>
                      <p className="text-[#0a0a0a] text-sm leading-relaxed line-clamp-2" dir={language === 'ar' ? 'rtl' : 'ltr'}>
                        "{post.text}"
                      </p>
                    </div>
                  )}

                  {/* Author & Share */}
                  <div className={`flex items-center justify-between pt-3 border-t border-gray-100`}>
                    <div className={`flex items-center gap-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                      <div className="w-8 h-8 rounded-full bg-[#009639] flex items-center justify-center shadow-sm">
                        <User className="w-4 h-4 text-white" />
                      </div>
                      <div className={language === 'ar' ? 'text-right' : 'text-left'}>
                        <p className="font-medium text-[#0a0a0a] text-sm">{post.name}</p>
                        <p className="text-gray-400 text-xs">{formatDate(post.created_at)}</p>
                      </div>
                    </div>
                    
                    {/* Share Button */}
                    <div className="relative">
                      <button
                        onClick={() => setExpandedShare(expandedShare === post.id ? null : post.id)}
                        className="w-8 h-8 rounded-full bg-gray-100 hover:bg-[#009639]/10 flex items-center justify-center transition-colors"
                        data-testid={`share-btn-${post.id}`}
                      >
                        <Share2 className="w-4 h-4 text-gray-500 hover:text-[#009639]" />
                      </button>
                      
                      {/* Share dropdown */}
                      {expandedShare === post.id && (
                        <div className="absolute bottom-full right-0 mb-2 bg-white rounded-lg shadow-xl p-2 border border-gray-100 z-20">
                          <SocialShare post={post} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
